import axios from 'axios'
import { API_BASE_URL } from '../config.js'
import { useApiStatus } from '../composables/useApiStatus.js'
import { handleExpiredSession, shouldAttemptRefresh } from './authExpiry.js'

const { setApiUnavailable, setApiAvailable } = useApiStatus()

// Single-flight refresh: many requests can 401 at once when the access token
// expires; they all await the same /api/auth/refresh call instead of stampeding.
let refreshPromise = null
function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/api/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        if (res?.data?.user) {
          localStorage.setItem('user', JSON.stringify(res.data.user))
        }
        return true
      })
      .catch(() => false)
      .finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

function setupErrorInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => {
      setApiAvailable()
      return response
    },
    async (error) => {
      // Try to silently mint a new access token (via the refresh cookie) and
      // replay the request, so an expired session self-heals without a re-login.
      if (shouldAttemptRefresh(error)) {
        error.config._retried = true
        if (await refreshSession()) {
          return instance(error.config)
        }
      }
      handleExpiredSession(error) // refresh not applicable or failed -> fallback
      if (!error.response) {
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          setApiUnavailable('downtime')
        } else if (error.message?.includes('Network Error') || error.message?.includes('Failed to fetch')) {
          setApiUnavailable('downtime')
        }
      } else if (error.response.status >= 500) {
        setApiUnavailable('bug')
      } else if (error.response.status === 0) {
        setApiUnavailable('downtime')
      } else {
        setApiAvailable()
      }
      return Promise.reject(error)
    }
  )
}

export function createAxiosInstance(config = {}) {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      ...config.headers
    },
    withCredentials: true,
    ...config
  })

  setupErrorInterceptor(instance)
  return instance
}

export function createBaseAxiosInstance() {
  return createAxiosInstance()
}